/* eslint-disable no-sequences */
/* eslint-disable no-return-assign */
import {ApolloClient, ApolloProvider, from} from "@apollo/client";
import React, {ReactElement} from "react";
import {createRoot} from "react-dom/client";
import {App} from "./app";
import {BrowserRouter} from "react-router-dom";
import LogRocket from "logrocket";
import {cache} from "./cache";
import {config} from "./config";
import {createUploadLink} from "apollo-upload-client";
import {resolvers} from "./graphql/resolvers/resolvers";
import {setContext} from "@apollo/client/link/context";
import {TouchBackend} from "react-dnd-touch-backend";
import {DndProvider} from "react-dnd";
import {onError} from "@apollo/client/link/error";
import setupLogRocketReact from "logrocket-react";
import {IntercomProvider} from "react-use-intercom";
import "firebase/firestore";
import {auth} from "./utils/firebase";
import {v4 as uuid} from "uuid";
import {ErrorBoundary} from "./components/ErrorBoundary";
import {FliptProvider} from "@flipt-io/flipt-client-react";

const RouterWrapper = ({children}: {children: React.ReactNode}): ReactElement => {
	return <BrowserRouter>{children}</BrowserRouter>;
};

/**
 * Setup component
 *
 * @returns our app with the ApolloClient and RouterWrapper wrapper components
 */
const Setup = (): ReactElement => {
	const env = config.environment;
	// setup logging services
	if (env !== "dev") {
		if (config.logRocketProjectName) LogRocket.init(config.logRocketProjectName);
		setupLogRocketReact(LogRocket);
	}
	const uploadLink = createUploadLink({
		uri: `${config.apiHost}/graphql`,
		credentials: "include",
	});
	const reportError = (error): void => {
		if (env === "dev") {
			// eslint-disable-next-line no-console
			console.error(error);
		}
	};
	const bearerLink: any = setContext(async (_, {headers}) => {
		const user = auth?.currentUser;
		let token: string | null = null;

		if (user) {
			token = await user.getIdToken();
			localStorage.setItem("token", token);
		}
		const storageToken = localStorage.getItem("token");
		const idToken = token || storageToken;
		return {
			headers: {
				...headers,
				authorization: token ? `Bearer ${idToken}` : "",
				"X-Request-ID": uuid(),
			},
		};
	});

	const concattedLink = bearerLink.concat(uploadLink);
	const errorLink = onError(({graphQLErrors, networkError}) => {
		if (graphQLErrors) {
			graphQLErrors.forEach(({message, locations, path}) => {
				reportError(
					new Error(`[GraphQL error]: Message: ${message},
					 Location: ${JSON.stringify(locations)},
					 Path: ${path}`)
				);
			});
		}
		if (networkError) reportError(new Error(`[Network error]: ${networkError}`));
	});
	const client = new ApolloClient({
		link: from([errorLink, concattedLink]),
		cache,
		resolvers,
		connectToDevTools: true,
	});

	if (client === undefined) {
		return <div>Loading...</div>;
	}

	return (
		<ErrorBoundary>
			<ApolloProvider client={client}>
				<FliptProvider
					options={{
						url: config.fliptConfig.url,
						authentication: {
							clientToken: config.fliptConfig.clientToken,
						},
					}}
				>
					<RouterWrapper>
						<DndProvider backend={TouchBackend} options={{enableMouseEvents: true}}>
							<IntercomProvider appId={config.intercomAppId}>
								<App />
							</IntercomProvider>
						</DndProvider>
					</RouterWrapper>
				</FliptProvider>
			</ApolloProvider>
		</ErrorBoundary>
	);
};
const container = document.getElementById("root");
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);
root.render(<Setup />);
